<script setup lang="ts">
import { computed, type PropType, ref, toRef } from 'vue';
import Dialog from 'primevue/dialog';
import { ButtonType, CustomButton, RichInput } from '@/components';
import { type FormField } from '@/models';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  field: {
    type: Object as PropType<FormField>,
    required: true
  }
});
const initialField = toRef<FormField>(props.field);
const localField = ref<FormField>(JSON.parse(JSON.stringify(props.field)));

const initialChoicesText = ref(localField.value.options.choices!.map(choice => choice.label).join('\n').trimEnd());
const choicesText = ref(localField.value.options.choices!.map(choice => choice.label).join('\n').trimEnd());

const emits = defineEmits(['onClose', 'onSave']);

const closeDialog = () => {
  emits('onClose');
};


const isPristine = computed(() => JSON.stringify(initialChoicesText.value) !== JSON.stringify(choicesText.value));

const saveChoices = () => {
  const splitted = choicesText.value
    .split('\n')
    .map(label => label.trim())
    .filter(label => label.length > 0);
  const choices = splitted.map((label, idx) => ({ id: idx + 1, label, value: idx + 1 }));
  initialField.value.options.choices = choices;
  closeDialog();
};
</script>

<template>
    <div class="dialog-root">
        <Dialog :visible="visible"
            header="Edit choices list"
            modal
            class="dialog"
            append-to="self"
            @update:visible="closeDialog">
            <div class="dialog-content">
                <div class="choice-description">
                    Type or paste the choices list. Make sure that the different choices are separated by a new line between each other.
                </div>
                <div class="choices-input">
                    <RichInput v-model="choicesText" :visible-border="true"/>
                </div>
                <div class="warning-block">
                <h2>
                  <FontAwesomeIcon :icon="[`far`, `exclamation-triangle`]"
                    class="exclamation-icon" />
                  Risk of data loss
                </h2>
                <p>
                  When adding new choices always add them at the <span>bottom of the list.</span>
                  Otherwise, you will affect the data that has already been collected.
                </p>
              </div>
            </div>
            <div class="footer">
              <div class="empty"></div>
              <div class="footer-buttons">
                <CustomButton text="Cancel"
                  @onClick="closeDialog"
                  :margin-left="16"
                  :type="ButtonType.OUTLINED"/>
                <CustomButton text="Save"
                  @onClick="saveChoices"
                  :disabled="!isPristine"
                  :margin-left="16"
                  :type="ButtonType.POSITIVE"/>
              </div>
            </div>
        </Dialog>
    </div>
</template>

<style scoped lang="scss">
:deep(.p-dialog) {
  border-radius: 4px;
}
:deep(.p-dialog-header) {
  padding: unset;
  padding: 40px 40px 0px 40px;
}
:deep(.p-dialog-content) {
  padding: unset;
}
.dialog-content {
  width: 600px;
  padding: 8px 40px 40px 40px;
}
.choices-input {
  margin: 20px 0px 20px 0px;
}
.warning-block {
  margin-bottom: 40px;
  padding: 12px;
  border: 1px solid #ea3546;
  border-radius: 4px;
  background-color: #fef5f6;
  color: #5c0a11;
  h2 {
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    margin: 0 0 4px 0;
    .exclamation-icon {
      color: #ea3546;
      line-height: 14px;
      font-size: 16px;
      margin-right: 8px;
    }
  }
  p {
    margin: 0,
  }
}
.footer {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 9999;

  .footer-buttons {
    display: flex;
  }
}
</style>
